import OrderCargoOrderDetails, {
  OrderCargoOrderDetailsCargoOrder,
  OrderCargoOrderDetailsCargoOrderCargoSource,
  OrderCargoOrderDetailsCargoOrderCargoSourceElement,
  OrderCargoOrderDetailsCargoOrderRide,
  OrderCargoOrderDetailsCargoOrderRideMeta,
  OrderCargoOrderDetailsCargoOrderRideMetaCargoSubject,
  OrderCargoOrderDetailsCargoOrderRideMetaCargoSubjectList,
  OrderCargoOrderDetailsCargoOrderRideSeq,
  OrderCargoOrderDetailsCargoOrderRideSeqNode,
  OrderCargoOrderDetailsCargoOrderTransportingOrder,
  OrderCargoOrderDetailsCargoOrderTransportingOrderSolvedOrder,
  OrderCargoOrderDetailsCargoOrderTransportingOrderSolvedOrderRide,
  OrderCargoOrderDetailsCargoOrderTransportingOrderSolvedOrderRideSeqItem,
  OrderCargoOrderDetailsCargoOrderTransportingOrderSolvedOrderRideSeqItemMeta,
  OrderCargoOrderDetailsCargoOrderTransportingOrderSolvedOrderRideSeqItemMetaSubject,
  OrderCargoOrderDetailsCargoOrderTransportingOrderSolvedOrderRideSeqItemMetaSubjectList,
  OrderCargoOrderDetailsNodeExclusion,
  OrderCargoOrderDetailsPassenger,
} from "../../../../../common/services/order/cargo-order/details/order-cargo-order-details";
import OrderJoin, {
  OrderJoinCargoItemsSourceElement,
  OrderJoinCargoItemsSourceElementCargoItemSource,
  OrderJoinCargoOrder,
  OrderJoinCargoOrderRide,
  OrderJoinNodeExclusion,
  OrderJoinPreviewPassenger,
  OrderJoinPreviewRideSeqItem,
  OrderJoinPreviewRideWaypoint,
  OrderJoinPreviewRideWaypointGroup,
  OrderJoinPreviewRideWaypointMeta,
  OrderJoinPreviewRideWaypointMetaSubject,
  OrderJoinPreviewRideWaypointMetaSubjectList,
  OrderJoinSolvedOrder,
  OrderJoinSolvedOrderRide,
  OrderJoinSolvedOrderRideSeqItem,
  OrderJoinSolvedOrderRideSeqItemMeta,
  OrderJoinSolvedOrderRideSeqItemMetaSubject,
  OrderJoinSolvedOrderRideSeqItemMetaSubjectList,
  OrderJoinTransportingOrder,
} from "../../../../../common/services/order/join/order-join";
import SourceType from "../../../../../common/types/source-type";

const createCargoOrderRideSeqItemWaypointMetaSubject = (
  subject: OrderCargoOrderDetailsCargoOrderRideMetaCargoSubject,
): OrderJoinPreviewRideWaypointMetaSubject => {
  return {
    id: subject.uuid,
  };
};

const createCargoOrderRideSeqItemWaypointMetaSubjects = (
  subjects: OrderCargoOrderDetailsCargoOrderRideMetaCargoSubject[],
): OrderJoinPreviewRideWaypointMetaSubject[] => {
  return subjects.map(createCargoOrderRideSeqItemWaypointMetaSubject);
};

const createCargoOrderRideSeqItemWaypointMetaSubjectList = (
  subjectList: OrderCargoOrderDetailsCargoOrderRideMetaCargoSubjectList,
): OrderJoinPreviewRideWaypointMetaSubjectList => {
  return {
    subjects: createCargoOrderRideSeqItemWaypointMetaSubjects(
      subjectList.subjects,
    ),
  };
};

const createCargoOrderRideSeqItemWaypointMeta = (
  meta: OrderCargoOrderDetailsCargoOrderRideMeta,
): OrderJoinPreviewRideWaypointMeta => {
  return {
    cargoEnter: createCargoOrderRideSeqItemWaypointMetaSubjectList(
      meta.cargoEnter,
    ),
    cargoExit: createCargoOrderRideSeqItemWaypointMetaSubjectList(
      meta.cargoExit,
    ),
    id: meta.uuid,
  };
};

const createCargoOrderRideSeqItemWaypoint = (
  waypoint: OrderCargoOrderDetailsCargoOrderRideSeqNode,
): OrderJoinPreviewRideWaypoint => {
  return {
    displayName: waypoint.displayName,
    lat: waypoint.lat,
    lon: waypoint.lon,
    time: waypoint.time,
    haltingTime: waypoint.haltingTime,
    meta: createCargoOrderRideSeqItemWaypointMeta(waypoint.meta),
  };
};

const createCargoOrderRideSeqItem = (
  seqItem: OrderCargoOrderDetailsCargoOrderRideSeq,
): OrderJoinPreviewRideSeqItem => {
  if ("seq" in seqItem) {
    const group: OrderJoinPreviewRideWaypointGroup = {
      seq: seqItem.seq.map(createCargoOrderRideSeqItemWaypoint),
      sequenceOrder: seqItem.sequenceOrder,
    };
    return group;
  } else {
    return createCargoOrderRideSeqItemWaypoint(seqItem);
  }
};

const createCargoOrderRideSeq = (
  seq: OrderCargoOrderDetailsCargoOrderRideSeq[],
): OrderJoinPreviewRideSeqItem[] => {
  return seq.map(createCargoOrderRideSeqItem);
};

const createCargoOrderRide = (
  ride: OrderCargoOrderDetailsCargoOrderRide,
): OrderJoinCargoOrderRide => {
  return {
    seq: createCargoOrderRideSeq(ride.seq),
    sequenceOrder: ride.sequenceOrder,
  };
};

const createCargoOrderTransportingOrderSolvedOrderRideSeqItemMetaSubject = (
  subject: OrderCargoOrderDetailsCargoOrderTransportingOrderSolvedOrderRideSeqItemMetaSubject,
): OrderJoinSolvedOrderRideSeqItemMetaSubject => {
  return {
    id: subject.id,
  };
};

const createCargoOrderTransportingOrderSolvedOrderRideSeqItemMetaSubjects = (
  subjects: OrderCargoOrderDetailsCargoOrderTransportingOrderSolvedOrderRideSeqItemMetaSubject[],
): OrderJoinSolvedOrderRideSeqItemMetaSubject[] => {
  return subjects.map(
    createCargoOrderTransportingOrderSolvedOrderRideSeqItemMetaSubject,
  );
};

const createCargoOrderTransportingOrderSolvedOrderRideSeqItemMetaSubjectList = (
  subjectList: OrderCargoOrderDetailsCargoOrderTransportingOrderSolvedOrderRideSeqItemMetaSubjectList,
): OrderJoinSolvedOrderRideSeqItemMetaSubjectList => {
  return {
    subjects:
      createCargoOrderTransportingOrderSolvedOrderRideSeqItemMetaSubjects(
        subjectList.subjects,
      ),
  };
};

const createCargoOrderTransportingOrderSolvedOrderRideSeqItemMeta = (
  meta: OrderCargoOrderDetailsCargoOrderTransportingOrderSolvedOrderRideSeqItemMeta,
): OrderJoinSolvedOrderRideSeqItemMeta => {
  return {
    cargoEnter:
      createCargoOrderTransportingOrderSolvedOrderRideSeqItemMetaSubjectList(
        meta.cargoEnter,
      ),
    cargoExit:
      createCargoOrderTransportingOrderSolvedOrderRideSeqItemMetaSubjectList(
        meta.cargoExit,
      ),
    estimatedTime: meta.estimatedTime,
  };
};

const createCargoOrderTransportingOrderSolvedOrderRideSeqItem = (
  seqItem: OrderCargoOrderDetailsCargoOrderTransportingOrderSolvedOrderRideSeqItem,
): OrderJoinSolvedOrderRideSeqItem => {
  return {
    displayName: seqItem.displayName,
    haltingTime: seqItem.haltingTime,
    lat: seqItem.lat,
    lon: seqItem.lon,
    meta: createCargoOrderTransportingOrderSolvedOrderRideSeqItemMeta(
      seqItem.meta,
    ),
    time: seqItem.time,
  };
};

const createCargoOrderTransportingOrderSolvedOrderRideSeq = (
  seq: OrderCargoOrderDetailsCargoOrderTransportingOrderSolvedOrderRideSeqItem[],
): OrderJoinSolvedOrderRideSeqItem[] => {
  return seq.map(createCargoOrderTransportingOrderSolvedOrderRideSeqItem);
};

const createCargoOrderTransportingOrderSolvedOrderRide = (
  ride: OrderCargoOrderDetailsCargoOrderTransportingOrderSolvedOrderRide,
): OrderJoinSolvedOrderRide => {
  return {
    seq: createCargoOrderTransportingOrderSolvedOrderRideSeq(ride.seq),
  };
};

const createCargoOrderTransportingOrderSolvedOrder = (
  solvedOrder: OrderCargoOrderDetailsCargoOrderTransportingOrderSolvedOrder | null,
): OrderJoinSolvedOrder | null => {
  if (!solvedOrder) {
    return null;
  }

  return {
    ride: createCargoOrderTransportingOrderSolvedOrderRide(solvedOrder.ride),
  };
};

const createCargoOrderTransportingOrder = (
  transportingOrder: OrderCargoOrderDetailsCargoOrderTransportingOrder,
): OrderJoinTransportingOrder => {
  return {
    createPlanEntryEntries: transportingOrder.createPlanEntryEntries,
    solvedOrder: createCargoOrderTransportingOrderSolvedOrder(
      transportingOrder.solvedOrder,
    ),
  };
};

const createCargoOrderTransportingOrders = (
  transportingOrders: OrderCargoOrderDetailsCargoOrderTransportingOrder[],
): OrderJoinTransportingOrder[] => {
  return transportingOrders.map(createCargoOrderTransportingOrder);
};

const createCargoOrderCargoItemsSourceListItemSource = (
  source: OrderCargoOrderDetailsCargoOrderCargoSource,
): OrderJoinCargoItemsSourceElementCargoItemSource => {
  if (source.sourceType == SourceType.EXTERNAL) {
    return {
      sourceType: SourceType.EXTERNAL,
      firstName: source.firstName,
      lastName: source.lastName,
    };
  } else {
    return {
      sourceType: SourceType.INTERNAL,
    };
  }
};

const createCargoOrderCargoItemsSourceListItem = (
  sourceListItem: OrderCargoOrderDetailsCargoOrderCargoSourceElement,
): OrderJoinCargoItemsSourceElement => {
  return {
    cargoItemSource: createCargoOrderCargoItemsSourceListItemSource(
      sourceListItem.cargoItemSource,
    ),
    cargoSubjectId: sourceListItem.cargoSubjectUuid,
  };
};

const createCargoOrderCargoItemsSourceList = (
  sourceList: OrderCargoOrderDetailsCargoOrderCargoSourceElement[],
): OrderJoinCargoItemsSourceElement[] => {
  return sourceList.map(createCargoOrderCargoItemsSourceListItem);
};

const createCargoOrder = (
  cargoOrder: OrderCargoOrderDetailsCargoOrder,
): OrderJoinCargoOrder => {
  return {
    id: cargoOrder.id,
    humanId: cargoOrder.humanId,
    ride: createCargoOrderRide(cargoOrder.ride),
    transportingOrders: createCargoOrderTransportingOrders(
      cargoOrder.transportingOrders,
    ),
    cargoItemsSourceList: createCargoOrderCargoItemsSourceList(
      cargoOrder.cargoItemsSourceList,
    ),
  };
};

const createPassenger = (
  passenger: OrderCargoOrderDetailsPassenger,
): OrderJoinPreviewPassenger => {
  return {
    firstName: passenger.firstName,
    id: passenger.uuid,
    lastName: passenger.lastName,
  };
};

const createPassengers = (
  passengers: OrderCargoOrderDetailsPassenger[],
): OrderJoinPreviewPassenger[] => {
  return passengers.map(createPassenger);
};

const createNodeExclusion = (
  nodeExclusion: OrderCargoOrderDetailsNodeExclusion,
): OrderJoinNodeExclusion => {
  return {
    excludeApproaching: nodeExclusion.excludeApproaching,
    excludeInside: nodeExclusion.excludeInside,
    excludeReturning: nodeExclusion.excludeReturning,
  };
};

const create = (details: OrderCargoOrderDetails): OrderJoin => {
  return {
    cargoOrder: createCargoOrder(details.cargoOrder),
    passengers: createPassengers(details.passengers),
    drivers: details.drivers,
    nodeExclusion: createNodeExclusion(details.nodeExclusion),
  };
};

const orderJoinFactory = {
  create,
};

export default orderJoinFactory;
